import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Check, CreditCard, LogOut, RotateCcw, Sparkles } from "lucide-react";
import { Badge, Button, Card, ErrorState, PageLoader, SectionTitle, Spinner, TextField, cx } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api, ApiError } from "@/lib/api";
import { useLogout, useSession } from "@/lib/session";
import { useAction } from "@/lib/queries";
import { isNative } from "@/lib/native";
import { buyStorePlan, loadStorePlans, manageStoreSubscription, purchaseCancelled, reportStorePurchases, storeName, storeProvider, type StorePlan } from "@/lib/store";
import { DataCard } from "./More";
import { formatPrice, perMonth } from "@shared/billing";
import type { BillingDTO, BillingPlan, PlanPriceDTO, StoreProvider } from "@shared/types";

const DAY = 86_400_000;

const INCLUDED = [
  "2 parents and 2 kids (more kids at a small extra cost)",
  "Responsibilities, jobs and kid-pitched jobs",
  "Cash, screen time, family store and savings goals",
  "Fair consequences with work-it-off",
  "Built-in suggestions for prices, caps and rewards",
  "Private by design: no ads, no tracking, no AI in the app",
  "Works on every phone, tablet and computer",
];

function longDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function useBilling() {
  return useQuery<BillingDTO, ApiError>({ queryKey: ["billing"], queryFn: () => api.get<BillingDTO>("/billing") });
}

/** Choose a plan and start the free trial (card up front), or manage an existing subscription. */
export function BillingPage() {
  const { family, me } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const toast = useToast();
  const logout = useLogout();
  const billing = useBilling();
  const [syncing, setSyncing] = useState(params.get("checkout") === "success");
  const synced = useRef(false);

  // Back from Stripe Checkout: record the subscription right away, then carry on into the app.
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (params.get("checkout") !== "success" || !sessionId || synced.current) return;
    synced.current = true;
    api
      .post<BillingDTO>("/billing/sync", { sessionId })
      .then(async (result) => {
        qc.setQueryData(["billing"], result);
        await qc.invalidateQueries({ queryKey: ["me"] });
        if (result.active) {
          toast.show(result.status === "trialing" ? "Your free trial has started 🎉" : "You're all set 🎉", "success");
          navigate(family.onboarded ? "/parent" : "/parent/setup", { replace: true });
        } else {
          setSyncing(false);
          setParams({}, { replace: true });
        }
      })
      .catch((err: ApiError) => {
        toast.show(err.message, "error");
        setSyncing(false);
        setParams({}, { replace: true });
      });
  }, [params, setParams, qc, navigate, toast, family.onboarded]);

  // Back from the customer portal: pick up plan changes or cancellations.
  useEffect(() => {
    if (!params.get("portal")) return;
    void api
      .post<BillingDTO>("/billing/sync")
      .then(async (result) => {
        qc.setQueryData(["billing"], result);
        await qc.invalidateQueries({ queryKey: ["me"] });
      })
      .catch(() => {})
      .finally(() => setParams({}, { replace: true }));
  }, [params, setParams, qc]);

  if (syncing) {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <Spinner className="size-8" />
          <p className="text-lg font-bold">Setting up your plan…</p>
        </div>
      </Shell>
    );
  }
  if (billing.isLoading) return <PageLoader />;
  if (billing.error || !billing.data) {
    return (
      <Shell>
        <ErrorState error={billing.error} onRetry={() => billing.refetch()} />
      </Shell>
    );
  }
  const b = billing.data;
  const signOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <Shell
      back={b.active ? () => navigate("/parent/more") : undefined}
      action={
        !b.active && (
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="size-4" /> Sign out
          </Button>
        )
      }
    >
      {b.active ? <CurrentPlan billing={b} /> : isNative() ? <ChooseStorePlan billing={b} familyName={family.name} /> : <ChoosePlan billing={b} familyName={family.name} />}
      {!b.active && me.user.role === "parent" && (
        <>
          <SectionTitle>Your data</SectionTitle>
          <DataCard />
        </>
      )}
    </Shell>
  );
}

function Shell({ children, back, action }: { children: React.ReactNode; back?: () => void; action?: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-canvas">
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-[calc(env(safe-area-inset-top)+16px)]">
        <div className="mb-6 flex items-center gap-2">
          {back ? (
            <button type="button" onClick={back} aria-label="Back" className="-ml-2 flex size-10 items-center justify-center rounded-full hover:bg-sunken">
              <ArrowLeft className="size-6" />
            </button>
          ) : (
            <img src="/favicon.svg" alt="" className="size-9" />
          )}
          <span className="font-black tracking-tight">Meaningful Rewards</span>
          <div className="ml-auto">{action}</div>
        </div>
        {children}
      </div>
    </div>
  );
}

/** What a plan costs per period for this many extra kids. */
const planTotal = (p: PlanPriceDTO, extraKids: number) => p.amount + p.extraKidAmount * extraKids;
const periodWord = (p: PlanPriceDTO) => (p.period === "year" ? "year" : "quarter");

function ChoosePlan({ billing, familyName }: { billing: BillingDTO; familyName: string }) {
  const [plan, setPlan] = useState<BillingPlan>("annual");
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const plans = billing.plans;
  const lapsed = !!billing.status;
  const trial = billing.trialEligible && billing.trialDays > 0;

  async function start() {
    setLoading(true);
    try {
      const { url } = await api.post<{ url: string }>("/billing/checkout", { plan });
      window.location.assign(url);
    } catch (err) {
      toast.show((err as ApiError).message, "error");
      setLoading(false);
    }
  }

  if (!plans) {
    return (
      <>
        <Card className="text-center">
          <p className="font-bold">Plans aren't available right now</p>
          <p className="mt-1 text-sm text-muted">Please try again in a few minutes.</p>
        </Card>
        <RedeemCode />
      </>
    );
  }
  const { quarterly, annual } = plans;
  const extra = billing.extraKids;
  const freeMonths = Math.round((planTotal(quarterly, 0) * 4 - planTotal(annual, 0)) / (quarterly.amount / 3));
  const chargeDate = Date.now() + billing.trialDays * DAY;
  const chosen = plans[plan];
  const total = formatPrice(planTotal(chosen, extra), chosen.currency);

  return (
    <div>
      <h1 className="text-3xl font-black leading-tight tracking-tight">
        {trial ? (
          <>
            Start your <span className="text-brand">{billing.trialDays}-day free trial</span>
          </>
        ) : lapsed ? (
          "Welcome back! Pick a plan to continue"
        ) : (
          "Choose your plan"
        )}
      </h1>
      <p className="mt-2 text-muted">
        {lapsed && !trial
          ? `Everything ${familyName} set up is still here, ready when you are.`
          : "Build your household economy: responsibilities first, then kids go earn."}
      </p>

      <div className="mt-6 grid gap-3" role="radiogroup" aria-label="Plan">
        <PlanOption
          selected={plan === "annual"}
          onSelect={() => setPlan("annual")}
          title="Annual"
          price={`${formatPrice(perMonth(planTotal(annual, extra), "annual"), annual.currency)}/mo`}
          detail={`${formatPrice(planTotal(annual, extra), annual.currency)} billed once a year`}
          badge={freeMonths > 0 ? `${freeMonths} months free` : undefined}
        />
        <PlanOption
          selected={plan === "quarterly"}
          onSelect={() => setPlan("quarterly")}
          title="Quarterly"
          price={`${formatPrice(perMonth(planTotal(quarterly, extra), "quarterly"), quarterly.currency)}/mo`}
          detail={`${formatPrice(planTotal(quarterly, extra), quarterly.currency)} billed every 3 months`}
        />
      </div>
      <p className="mt-3 text-sm text-muted">
        Includes {billing.includedParents} parents and {billing.includedKids} kids. Each additional kid is{" "}
        {formatPrice(chosen.extraKidAmount, chosen.currency)}/{periodWord(chosen)}
        {extra > 0 ? ` (you have ${billing.kidCount} kids, so ${extra} extra ${extra === 1 ? "is" : "are"} included above)` : ""}.
      </p>

      <Button size="lg" block className="mt-5" onClick={start} loading={loading}>
        <CreditCard className="size-5" /> {trial ? "Start free trial" : "Continue to payment"}
      </Button>
      <p className="mt-3 text-center text-sm text-muted">
        {trial
          ? `Card required. You won't be charged until ${longDate(chargeDate)}. Then ${total} per ${periodWord(chosen)}, renewing automatically until you cancel. Cancel anytime before then and you won't be charged.`
          : `${total} per ${periodWord(chosen)}, renewing automatically until you cancel. Cancel anytime; you keep access until the end of the period.`}{" "}
        <a href="/terms" target="_blank" className="font-bold text-brand underline">
          Terms
        </a>
      </p>
      <RedeemCode />

      <Card className="mt-6">
        <p className="mb-2 flex items-center gap-2 font-extrabold">
          <Sparkles className="size-4 text-brand" /> Everything included
        </p>
        <ul className="space-y-1.5 text-sm">
          {INCLUDED.map((item) => (
            <li key={item} className="flex gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-brand" /> {item}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** The plan screen in the iOS and Android apps: one family price, bought through the store. */
function ChooseStorePlan({ billing, familyName }: { billing: BillingDTO; familyName: string }) {
  const { family } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const plans = useQuery<StorePlan[], Error>({ queryKey: ["store-plans"], queryFn: loadStorePlans, retry: false });
  const [selected, setSelected] = useState<BillingPlan>("annual");
  const [busy, setBusy] = useState<"buy" | "restore" | null>(null);
  const finish = async (result: BillingDTO) => {
    qc.setQueryData(["billing"], result);
    await qc.invalidateQueries({ queryKey: ["me"] });
    if (result.active) navigate(family.onboarded ? "/parent" : "/parent/setup", { replace: true });
  };

  // A purchase the app couldn't report at the time (no signal, closed too soon) unlocks the family now.
  useEffect(() => {
    void reportStorePurchases()
      .then((result) => (result ? finish(result) : undefined))
      .catch(() => {});
  }, []);

  async function buy(plan: StorePlan) {
    setBusy("buy");
    try {
      await finish(await buyStorePlan(plan, billing.storeAccountToken!));
    } catch (err) {
      if (!purchaseCancelled(err)) toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  async function restore() {
    setBusy("restore");
    try {
      const result = await reportStorePurchases({ prompt: true });
      if (result?.active) await finish(result);
      else toast.show(`No active subscription found on this ${storeName()} account.`, "error");
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  const chosen = plans.data?.find((p) => p.plan === selected) ?? plans.data?.[0];
  const trialDays = chosen?.trialDays && billing.trialEligible ? chosen.trialDays : null;
  const period = chosen?.plan === "quarterly" ? "3 months" : "year";
  const lapsed = !!billing.status;

  return (
    <div>
      <h1 className="text-3xl font-black leading-tight tracking-tight">
        {trialDays ? (
          <>
            Start your <span className="text-brand">{trialDays}-day free trial</span>
          </>
        ) : lapsed ? (
          "Welcome back! Pick a plan to continue"
        ) : (
          "Choose your plan"
        )}
      </h1>
      <p className="mt-2 text-muted">
        {lapsed ? `Everything ${familyName} set up is still here, ready when you are.` : "Build your household economy: responsibilities first, then kids go earn."}
      </p>

      {plans.isLoading && (
        <div className="mt-8 flex justify-center">
          <Spinner className="size-8" />
        </div>
      )}
      {!plans.isLoading && !plans.data?.length && (
        <Card className="mt-6 text-center">
          <p className="font-bold">Plans aren't available right now</p>
          <p className="mt-1 text-sm text-muted">We couldn't reach the {storeName()}. Check your connection and try again.</p>
          <Button variant="secondary" className="mt-3" onClick={() => plans.refetch()}>
            Try again
          </Button>
        </Card>
      )}
      {chosen && (
        <>
          <div className="mt-6 grid gap-3" role="radiogroup" aria-label="Plan">
            {plans.data!.map((p) => (
              <PlanOption
                key={p.plan}
                selected={p.plan === chosen.plan}
                onSelect={() => setSelected(p.plan)}
                title={p.plan === "annual" ? "Annual" : "Quarterly"}
                price={p.price}
                detail={p.plan === "annual" ? "Billed once a year" : "Billed every 3 months"}
              />
            ))}
          </div>
          <p className="mt-3 text-sm text-muted">Includes {billing.includedParents} parents and up to {billing.maxKids} kids.</p>
          <Button size="lg" block className="mt-5" onClick={() => buy(chosen)} loading={busy === "buy"} disabled={!!busy}>
            {trialDays ? "Start free trial" : "Subscribe"}
          </Button>
          <p className="mt-3 text-center text-sm text-muted">
            {trialDays ? `Free for ${trialDays} days, then ${chosen.price} every ${period}` : `${chosen.price} every ${period}`}, renewing automatically until you cancel at least 24 hours
            before the period ends. Charged to your {storeName()} account. Manage or cancel anytime in your {storeName()} settings.{" "}
            <a href="/terms" target="_blank" className="font-bold text-brand underline">
              Terms
            </a>{" "}
            ·{" "}
            <a href="/privacy" target="_blank" className="font-bold text-brand underline">
              Privacy
            </a>
          </p>
        </>
      )}
      <Button variant="ghost" block className="mt-3" onClick={restore} loading={busy === "restore"} disabled={!!busy}>
        <RotateCcw className="size-4" /> Restore purchases
      </Button>
      <p className="mt-3 text-center text-sm text-muted">Already pay for {familyName}? Sign in with the same family account and your plan carries over.</p>
    </div>
  );
}

/** Free-access codes from the app's owner, used in place of a plan. */
function RedeemCode() {
  const { family } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const redeem = useAction(
    async () => {
      const result = await api.post<BillingDTO>("/billing/redeem", { code });
      qc.setQueryData(["billing"], result);
      // The session has to know about the access before leaving, or it sends the parent right back here.
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
    {
      success: "Free access unlocked 🎉",
      onSuccess: () => navigate(family.onboarded ? "/parent" : "/parent/setup", { replace: true }),
    },
  );

  // App Store rules don't allow unlocking the app with codes from inside it; they're for the website.
  if (isNative()) return null;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="mx-auto mt-5 block text-sm font-bold text-brand underline">
        Have an access code?
      </button>
    );
  }
  return (
    <form
      className="mt-5 flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        redeem.mutate();
      }}
    >
      <div className="min-w-0 flex-1">
        <TextField
          label="Access code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
      </div>
      <Button type="submit" variant="secondary" size="lg" loading={redeem.isPending} disabled={!code.trim()}>
        Apply
      </Button>
    </form>
  );
}

function PlanOption(props: { selected: boolean; onSelect: () => void; title: string; price: string; detail: string; badge?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      onClick={props.onSelect}
      className={cx(
        "flex w-full items-center gap-3 rounded-3xl bg-card p-4 text-left shadow-card ring-2 transition",
        props.selected ? "ring-brand" : "ring-transparent hover:ring-line",
      )}
    >
      <span
        className={cx(
          "flex size-6 shrink-0 items-center justify-center rounded-full border-2",
          props.selected ? "border-brand bg-brand text-on-brand" : "border-line",
        )}
      >
        {props.selected && <Check className="size-4" />}
      </span>
      <span className="flex-1">
        <span className="flex flex-wrap items-center gap-2 font-extrabold">
          {props.title} {props.badge && <Badge tone="coin">{props.badge}</Badge>}
        </span>
        <span className="block text-sm text-muted">{props.detail}</span>
      </span>
      <span className="text-right font-black">{props.price}</span>
    </button>
  );
}

function CurrentPlan({ billing }: { billing: BillingDTO }) {
  const [opening, setOpening] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const plan = billing.plan ? billing.plans?.[billing.plan] : null;
  const fromStore = billing.source === "apple" || billing.source === "google";
  // Web prices don't apply to store plans, and the iOS and Android apps don't show web prices or the Stripe portal.
  const showWebBilling = !isNative() && !fromStore;
  const price = plan && showWebBilling ? `${formatPrice(planTotal(plan, billing.extraKids), plan.currency)}/${periodWord(plan)}` : null;
  const daysLeft = billing.trialEndsAt ? Math.max(0, Math.ceil((billing.trialEndsAt - Date.now()) / DAY)) : 0;
  const other: BillingPlan = billing.plan === "annual" ? "quarterly" : "annual";
  const otherPlan = billing.plans?.[other];
  const switchPlan = useAction(() => api.post<BillingDTO>("/billing/plan", { plan: other }), {
    success: other === "annual" ? "Switched to annual" : "Switched to quarterly",
    onSuccess: (b) => qc.setQueryData(["billing"], b),
  });

  async function manageInStore() {
    setOpening(true);
    try {
      await manageStoreSubscription();
      qc.setQueryData(["billing"], await api.post<BillingDTO>("/billing/sync"));
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setOpening(false);
    }
  }

  async function manage() {
    setOpening(true);
    try {
      const { url } = await api.post<{ url: string }>("/billing/portal");
      window.location.assign(url);
    } catch (err) {
      toast.show((err as ApiError).message, "error");
      setOpening(false);
    }
  }

  let headline: string;
  let detail: string | null = null;
  if (billing.exempt) {
    headline = "Complimentary access";
    detail = "Your family has free access to everything.";
  } else if (billing.status === "trialing") {
    headline = `Free trial: ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`;
    detail = billing.cancelAtPeriodEnd
      ? `Your trial ends ${longDate(billing.trialEndsAt!)} and you won't be charged.`
      : `Then ${price ?? "your plan"} starting ${longDate(billing.trialEndsAt!)}, renewing automatically until you cancel.`;
  } else {
    headline = `${billing.plan === "annual" ? "Annual" : "Quarterly"} plan${price ? ` · ${price}` : ""}`;
    if (billing.currentPeriodEnd) {
      detail = billing.cancelAtPeriodEnd
        ? `Cancelled. You have access until ${longDate(billing.currentPeriodEnd)}.`
        : `Renews ${longDate(billing.currentPeriodEnd)}.`;
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-black tracking-tight">Plan & billing</h1>
      {billing.status === "past_due" && (
        <Card className="mt-4 flex gap-3 bg-warn-soft ring-0">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warn" />
          <div>
            <p className="font-extrabold">Your last payment didn't go through</p>
            <p className="text-sm">{fromStore ? `Update your payment method with ${storeName(billing.source as StoreProvider)} to keep your family's access.` : "Update your card to keep your family's access."}</p>
          </div>
        </Card>
      )}
      <Card className="mt-4">
        <p className="text-xl font-black">{headline}</p>
        {detail && <p className="mt-1 text-muted">{detail}</p>}
        {plan && !billing.exempt && showWebBilling && (
          <p className="mt-2 text-sm text-muted">
            {billing.kidCount} {billing.kidCount === 1 ? "kid" : "kids"}: {billing.includedKids} included
            {billing.extraKids > 0
              ? `, ${billing.extraKids} extra at ${formatPrice(plan.extraKidAmount, plan.currency)}/${periodWord(plan)} each`
              : ""}
            . Adding or removing kids is prorated.
          </p>
        )}
        {isNative() && billing.source === storeProvider() && (
          <div className="mt-4">
            <Button variant="secondary" onClick={manageInStore} loading={opening}>
              Manage subscription
            </Button>
          </div>
        )}
        {billing.hasCustomer && showWebBilling && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={manage} loading={opening}>
              <CreditCard className="size-4" /> Manage billing
            </Button>
            {otherPlan && !billing.exempt && !billing.cancelAtPeriodEnd && (
              <Button
                variant="ghost"
                loading={switchPlan.isPending}
                onClick={() => {
                  const cost = `${formatPrice(planTotal(otherPlan, billing.extraKids), otherPlan.currency)}/${periodWord(otherPlan)}`;
                  const note = other === "annual" && billing.status !== "trialing" ? " The difference is charged now." : "";
                  if (window.confirm(`Switch to the ${other} plan (${cost})?${note}`)) switchPlan.mutate();
                }}
              >
                Switch to {other}
              </Button>
            )}
          </div>
        )}
      </Card>
      {billing.hasCustomer && showWebBilling && (
        <p className="mt-3 text-sm text-muted">Update your card, download invoices or cancel, securely with Stripe.</p>
      )}
      {fromStore && !(isNative() && billing.source === storeProvider()) && (
        <p className="mt-3 text-sm text-muted">Your plan is billed by {storeName(billing.source as StoreProvider)}. Change or cancel it in your {storeName(billing.source as StoreProvider)} account settings.</p>
      )}
      {isNative() && billing.source === "stripe" && <p className="mt-3 text-sm text-muted">Your plan is billed through our website. Sign in there to change or cancel it.</p>}
      {!billing.exempt && <RedeemCode />}
    </div>
  );
}
